import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import '@/styles/verified-encounters.css';

const EASE = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ delay, duration: 0.9, ease: EASE }}
  >
    {children}
  </motion.div>
);

const ShieldCheckIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3 4 6v6c0 4.4 3.2 8.4 8 9 4.8-.6 8-4.6 8-9V6l-8-3Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const VerifiedBadgeIcon = () => (
  <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2 9.5 4.2 6.3 4l-.4 3.2L3 9l1.6 2.7L3 14.3 5.9 16l.4 3.2 3.2-.2L12 22l2.5-3 3.2.2.4-3.2L21 14.3 19.4 11.6 21 9l-2.9-1.8L17.7 4l-3.2.2L12 2Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const VERIFICATION_INCLUDES = [
  'Profile completeness',
  'Access eligibility',
  'Invitation or referral source',
  'Creator or campaign source',
  'Admin review status',
  'Duplicate or risk checks',
  'Account standing',
  'Consent and conduct acknowledgements',
  'Booking or connection status',
  'Manual review outcome',
];

const STATUS_LIST = [
  {
    key: 'verified',
    label: 'Verified',
    meaning: 'Required checks have been completed and the encounter currently meets the active verification rules.',
    tone: 'verified',
  },
  {
    key: 'pending',
    label: 'Pending',
    meaning: 'The encounter is still waiting on information, review, or confirmation.',
    tone: 'pending',
  },
  {
    key: 'needs-review',
    label: 'Needs Review',
    meaning: 'Something requires manual admin attention before the encounter can be marked verified.',
    tone: 'review',
  },
  {
    key: 'not-eligible',
    label: 'Not Eligible',
    meaning: 'The encounter does not currently meet the active requirements for verification.',
    tone: 'ineligible',
  },
  {
    key: 'expired',
    label: 'Expired',
    meaning: 'The verification signal is no longer current and may need to be refreshed.',
    tone: 'expired',
  },
];

const FAQS = [
  {
    q: 'Is a Verified Encounter public?',
    a: 'No. Verified Encounter information should only be shown where it is useful inside the NYMPHÉ experience. Internal review details should remain private.',
  },
  {
    q: 'Does verified mean guaranteed safe?',
    a: 'No. Verification is a trust signal based on completed app checks. It supports safer decision-making, but it does not replace personal judgement, consent, boundaries, or common-sense safety practices.',
  },
  {
    q: 'Who can see the status?',
    a: 'Visibility depends on the app\u2019s role and permission settings. Members may see simple status labels, while more detailed review information should remain limited to approved admin users.',
  },
  {
    q: 'Can a status change?',
    a: 'Yes. A status may change if information expires, new details are added, an admin review is required, or eligibility rules change.',
  },
  {
    q: 'What if something looks wrong?',
    a: 'Members should contact NYMPHÉ support so the team can review the issue discreetly.',
  },
];

const FaqItem = ({ q, a, isOpen, onToggle, id }) => (
  <div className={`ve-faq__item${isOpen ? ' ve-faq__item--open' : ''}`} data-testid={`ve-faq-${id}`}>
    <button
      type="button"
      className="ve-faq__q"
      aria-expanded={isOpen}
      aria-controls={`ve-faq-panel-${id}`}
      id={`ve-faq-trigger-${id}`}
      onClick={onToggle}
    >
      <span>{q}</span>
      <span className="ve-faq__chev" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="panel"
          id={`ve-faq-panel-${id}`}
          role="region"
          aria-labelledby={`ve-faq-trigger-${id}`}
          className="ve-faq__a-wrap"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
        >
          <div className="ve-faq__a">{a}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const VerifiedEncountersPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* The authenticated app surface doesn't exist in this repo yet.
     "Return to App" sends the user back if they came from an in-app
     more-info tap (history-go-back). If there's no history (the page
     was opened directly), falls back to the placeholder /app route
     per the spec — when the authenticated shell ships, this stays
     correct without further changes. */
  const handleReturnToApp = (e) => {
    e.preventDefault();
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/app');
    }
  };

  return (
    <MotionConfig reducedMotion="user">
      <main className="ve-page" data-testid="verified-encounters-page">
        {/* ─── Hero ───────────────────────────────────────────── */}
        <section className="ve-hero" data-testid="ve-hero">
          <div className="ve-hero__glow" aria-hidden="true" />
          <div className="ve-container">
            <Reveal>
              <div className="ve-badge" data-testid="ve-trust-badge">
                <ShieldCheckIcon size={16} />
                <span>Verified Encounters · Trust layer</span>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="ve-h1" data-testid="ve-page-title">Verified Encounters</h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="ve-hero__sub">
                A discreet trust feature designed to help members understand when a
                connection has met NYMPHÉ&rsquo;s verification standards.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <p className="ve-hero__intro">
                Verified Encounters are designed to bring more confidence, transparency,
                and peace of mind to private connections &mdash; without compromising
                discretion. They help signal that key steps have been completed,
                reviewed, or confirmed according to NYMPHÉ&rsquo;s trust and safety
                standards.
              </p>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="ve-cta-row">
                <a
                  className="ve-btn ve-btn--primary"
                  href="/app"
                  onClick={handleReturnToApp}
                  data-testid="ve-hero-return-cta"
                >
                  Return to App
                </a>
                <a
                  className="ve-btn ve-btn--ghost"
                  href="mailto:hello@nymphe.co?subject=Verified%20Encounters%20Support"
                  data-testid="ve-hero-support-cta"
                >
                  Contact Support
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── 1. What is a Verified Encounter? ───────────────── */}
        <section className="ve-section" data-testid="ve-section-what">
          <div className="ve-container">
            <Reveal>
              <div className="ve-eyebrow">01 · Overview</div>
              <h2 className="ve-h2">What is a Verified Encounter?</h2>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="ve-section__body">
                A Verified Encounter is a trust signal that helps members understand
                when a connection, profile, booking, invitation, or access flow has
                passed selected verification checks inside NYMPHÉ.
              </p>
              <p className="ve-section__body">
                It is designed to support safer, more intentional interactions by
                confirming that important trust steps have been completed before,
                during, or after an encounter, depending on the app&rsquo;s available
                verification flow.
              </p>
            </Reveal>

            <div className="ve-card-row">
              {[
                { title: 'Authenticity', body: 'Helps confirm that key profile, access, or identity signals have been reviewed where required.' },
                { title: 'Discretion', body: 'Supports trust without exposing unnecessary private information to other members.' },
                { title: 'Confidence', body: 'Gives members a clearer sense of which connections have completed NYMPHÉ\u2019s verification pathway.' },
              ].map((c, i) => (
                <Reveal key={c.title} delay={0.12 + i * 0.06}>
                  <div className="ve-card" data-testid={`ve-card-${c.title.toLowerCase()}`}>
                    <div className="ve-card__mark" aria-hidden="true">
                      <ShieldCheckIcon />
                    </div>
                    <div className="ve-card__title">{c.title}</div>
                    <div className="ve-card__body">{c.body}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── 2. How it works (timeline) ─────────────────────── */}
        <section className="ve-section ve-timeline-section" data-testid="ve-section-how">
          <div className="ve-container">
            <Reveal>
              <div className="ve-eyebrow">02 · Process</div>
              <h2 className="ve-h2">How it works</h2>
            </Reveal>
            <ol className="ve-timeline">
              {[
                { n: '01', t: 'A connection enters the verification flow', b: 'When a member, creator, invitation, booking, or access request reaches a verification point, NYMPHÉ checks the required details against the current trust rules.' },
                { n: '02', t: 'Required signals are reviewed', b: 'The system may review approved details such as account status, invitation source, access eligibility, profile completeness, campaign source, moderation status, or admin review outcomes.' },
                { n: '03', t: 'The encounter receives a status', b: 'Once the required steps are complete, the connection may be marked as verified, pending, needs review, or not eligible, depending on the available data.' },
                { n: '04', t: 'Members see a clear trust signal', b: 'When appropriate, NYMPHÉ displays a simple Verified Encounter indicator so members can understand the status without needing to see sensitive internal details.' },
                { n: '05', t: 'Admins can review exceptions', b: 'If anything appears incomplete, duplicated, inconsistent, or blocked, the encounter can be held for manual review before being released or approved.' },
              ].map((s, i) => (
                <Reveal key={s.n} delay={0.06 + i * 0.06}>
                  <li className="ve-step" data-testid={`ve-step-${s.n}`}>
                    <div className="ve-step__num">{s.n}</div>
                    <div className="ve-step__body">
                      <div className="ve-step__title">{s.t}</div>
                      <div className="ve-step__copy">{s.b}</div>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </section>

        {/* ─── 3. What verification may include ───────────────── */}
        <section className="ve-section" data-testid="ve-section-includes">
          <div className="ve-container">
            <Reveal>
              <div className="ve-eyebrow">03 · Signals</div>
              <h2 className="ve-h2">What verification may include</h2>
              <p className="ve-section__body">
                The exact checks may vary depending on the type of connection and the
                current NYMPHÉ access rules. Verification may include one or more of
                the following signals.
              </p>
            </Reveal>

            <div className="ve-grid">
              {VERIFICATION_INCLUDES.map((item, i) => (
                <Reveal key={item} delay={0.04 + i * 0.03}>
                  <div className="ve-tile" data-testid={`ve-include-${item.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <span className="ve-tile__dot" aria-hidden="true" />
                    {item}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── 4. What it does not guarantee ──────────────────── */}
        <section className="ve-section" data-testid="ve-section-not">
          <div className="ve-container">
            <Reveal>
              <div className="ve-eyebrow">04 · Boundaries</div>
              <h2 className="ve-h2">What Verified Encounters do not guarantee</h2>
              <p className="ve-section__body">
                A Verified Encounter is a trust signal, not a guarantee. It does not
                promise chemistry, availability, behaviour, response time, personal
                compatibility, identity beyond completed app checks, or any outcome
                outside NYMPHÉ&rsquo;s verification process.
              </p>
            </Reveal>

            <Reveal delay={0.10}>
              <div className="ve-panel ve-panel--clarity" data-testid="ve-panel-clarity">
                <div className="ve-panel__title">Verified does not mean</div>
                <ul className="ve-panel__list">
                  <li>Guaranteed compatibility</li>
                  <li>Guaranteed availability</li>
                  <li>Guaranteed conduct</li>
                  <li>A background check unless explicitly stated</li>
                  <li>A replacement for personal judgement</li>
                  <li>A reason to ignore boundaries, consent, or safety practices</li>
                </ul>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── 5. Privacy & discretion ────────────────────────── */}
        <section className="ve-section" data-testid="ve-section-privacy">
          <div className="ve-container">
            <Reveal>
              <div className="ve-eyebrow">05 · Privacy</div>
              <h2 className="ve-h2">Private by design</h2>
              <p className="ve-section__body">
                Verified Encounters are built to support confidence while protecting
                discretion. NYMPHÉ should only display the trust signals members need
                to see &mdash; not sensitive internal notes, private admin data, or
                unnecessary personal information.
              </p>
            </Reveal>

            <div className="ve-card-row">
              {[
                { title: 'Minimal visibility', body: 'Members see simple status signals, not internal review details.' },
                { title: 'Controlled access', body: 'Sensitive review information should remain available only to approved admin roles.' },
                { title: 'Respectful design', body: 'The feature is designed to support trust without making the experience feel exposed, clinical, or intrusive.' },
              ].map((c, i) => (
                <Reveal key={c.title} delay={0.08 + i * 0.06}>
                  <div className="ve-card" data-testid={`ve-privacy-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <div className="ve-card__title">{c.title}</div>
                    <div className="ve-card__body">{c.body}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── 6. Status meanings ─────────────────────────────── */}
        <section className="ve-section" data-testid="ve-section-status">
          <div className="ve-container">
            <Reveal>
              <div className="ve-eyebrow">06 · Status</div>
              <h2 className="ve-h2">Status meanings</h2>
            </Reveal>
            <div className="ve-status-list">
              {STATUS_LIST.map((s, i) => (
                <Reveal key={s.key} delay={0.05 + i * 0.05}>
                  <div className={`ve-status ve-status--${s.tone}`} data-testid={`ve-status-${s.key}`}>
                    <div className="ve-status__chip">
                      <VerifiedBadgeIcon />
                      <span>{s.label}</span>
                    </div>
                    <p className="ve-status__copy">{s.meaning}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── 7. Why it matters ──────────────────────────────── */}
        <section className="ve-section" data-testid="ve-section-why">
          <div className="ve-container">
            <Reveal>
              <div className="ve-eyebrow">07 · Why</div>
              <h2 className="ve-h2">Why it matters</h2>
              <p className="ve-section__body">
                NYMPHÉ is designed for adults who value privacy, beauty, intention,
                and trust. Verified Encounters help create a more considered
                environment by reducing uncertainty and giving members a clearer
                sense of confidence before they proceed.
              </p>
            </Reveal>

            <div className="ve-card-row">
              {[
                'More clarity before connection',
                'Less uncertainty around access and approval',
                'A more refined trust experience for members, creators, and approved partners',
              ].map((t, i) => (
                <Reveal key={t} delay={0.08 + i * 0.06}>
                  <div className="ve-panel ve-panel--why" data-testid={`ve-why-${i + 1}`}>
                    <p className="ve-panel__copy">{t}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── 8. FAQ ─────────────────────────────────────────── */}
        <section className="ve-section" data-testid="ve-section-faq">
          <div className="ve-container ve-container--narrow">
            <Reveal>
              <div className="ve-eyebrow">08 · FAQ</div>
              <h2 className="ve-h2">Frequently asked</h2>
            </Reveal>
            <div className="ve-faq">
              {FAQS.map((f, i) => (
                <Reveal key={f.q} delay={0.05 + i * 0.04}>
                  <FaqItem
                    q={f.q}
                    a={f.a}
                    id={i + 1}
                    isOpen={openFaq === i}
                    onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                  />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── 9. Closing CTA + privacy note ──────────────────── */}
        <section className="ve-section ve-close" data-testid="ve-section-close">
          <div className="ve-container ve-container--narrow">
            <Reveal>
              <h3 className="ve-close__title">Trust, without losing the mystery.</h3>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="ve-close__copy">
                Verified Encounters are part of NYMPHÉ&rsquo;s commitment to more
                intentional, discreet, and refined private connections.
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="ve-cta-row ve-cta-row--centred">
                <a
                  className="ve-btn ve-btn--primary"
                  href="/app"
                  onClick={handleReturnToApp}
                  data-testid="ve-close-return-cta"
                >
                  Return to App
                </a>
                <a
                  className="ve-btn ve-btn--ghost"
                  href="mailto:hello@nymphe.co?subject=Verified%20Encounters%20Support"
                  data-testid="ve-close-support-cta"
                >
                  Contact Support
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.24}>
              <p className="ve-disclaimer" data-testid="ve-disclaimer">
                Verified Encounters are a NYMPHÉ trust signal based on available
                platform checks and review rules. They are not a guarantee of
                behaviour, compatibility, identity beyond completed app checks, or
                any outcome outside the platform.
              </p>
            </Reveal>
          </div>
        </section>
      </main>
    </MotionConfig>
  );
};

export default VerifiedEncountersPage;
